/**
 * Workspace settings (owners and admins): name, URL slug (validated with `logic/slug`; changing it warns that old
 * links stop working), icon (the photo pipeline: square crop → 512 px WebP → upload → attach) and the default
 * language, and the allowed email domains (people with a verified address there join without an invite; core
 * `logic/emailDomains`; each add/remove saves at once). Everyone else sees them read-only. Follows `workspace.updated` live. Online only (a refusal or a taken
 * slug shows a notice and keeps the draft).
 */
import type { FeatureAdminAccess, FeatureArea, HuddleLinkSettings, Locale, UpdateWorkspaceRequest, Workspace } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canSetFeature, FEATURE_AREAS, FEATURE_PARENT, isFeatureEnabled } from "../logic/features.ts";
import { huddleLinkSettings } from "../logic/huddlesArea.ts";
import { emailDomainProblem, MAX_EMAIL_DOMAINS, normalizeEmailDomain } from "../logic/emailDomains.ts";
import { canDeleteWorkspace, canEditWorkspace, roleContext } from "../logic/roles.ts";
import { isReservedSlug, isValidSlug } from "../logic/slug.ts";
import { errorKind, type ApiFailure, type CropArea, type PickedFile } from "../ports.ts";
import { IDLE_PHOTO, PhotoPipeline, type PhotoState, type SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type WorkspaceSettingsState = {
  status: "loading" | "ready" | "error";
  workspace?: Workspace;
  canEdit: boolean;
  /** Placeholder until workspace deletion ships: the primary owner (any owner without one). */
  canDelete: boolean;
  name: string;
  slug: string;
  defaultLocale: Locale;
  nameProblem?: MessageDescriptor;
  slugProblem?: MessageDescriptor;
  /** The slug is changing: old links (/{old}) stop working. */
  oldSlug?: string;
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  /** The icon shown (the new one while it uploads). */
  iconUrl?: string;
  photo: PhotoState;
  primaryOwnerName?: string;
  notice?: MessageDescriptor;
  /** Allowed email domains (undefined until loaded, or when I can't see them). */
  domains?: string[];
  /** The "add a domain" field. */
  domainDraft: string;
  /** Why the draft can't be added (shown once something is typed). */
  domainProblem?: MessageDescriptor;
  canAddDomain: boolean;
  /** Saving the domain list. */
  domainsSaving: boolean;
  /**
   * AI thread summaries on/off (docs/features/thread-summaries.md): absent when this server can't write them
   * (bootstrap `aiAvailable`). Saves at once; only owners and admins can change it.
   */
  aiSummaries?: { on: boolean; saving: boolean };
  /** Link previews under messages on/off (Slack "Show link previews"). Saves at once; owners and admins. */
  unfurls?: { on: boolean; saving: boolean };
  /** Members may add "Sent with Bundle" to their emails (logic/mailFooter). Saves at once; owners and admins. */
  mailSentWith?: { on: boolean; saving: boolean };
  /** Settings → Features: one row per product area (core logic/features), in display order. */
  features: FeatureRow[];
  /** The area being saved. */
  featureSaving?: FeatureArea;
  /** What owners and admins keep of switched-off areas. */
  featureAdminAccess: FeatureAdminAccess;
  /** Settings → Huddles: links to share and people outside the workspace (core logic/huddlesArea). */
  huddleLinks: HuddleLinkSettings;
  huddleLinksSaving: boolean;
  /** Chat is off: huddles start from the Huddles area. */
  chatOff: boolean;
};

/** One product area's switch. */
export type FeatureRow = {
  area: FeatureArea;
  /** Its own switch. */
  on: boolean;
  /** Its parent area while that is off (the switch is disabled: "Needs Email on"). */
  needs?: FeatureArea;
  /** On and the last usable area: it can't be turned off. */
  last: boolean;
};

export class WorkspaceSettingsViewModel extends ViewModel<WorkspaceSettingsState> {
  readonly ws: WorkspaceViewModel;
  private readonly deps: SettingsDeps;
  private readonly photos: PhotoPipeline;
  private takenSlug: string | undefined;

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps) {
    super({
      status: "loading", canEdit: false, canDelete: false, name: "", slug: "", defaultLocale: "en", dirty: false, canSave: false, saving: false, photo: IDLE_PHOTO,
      domainDraft: "", canAddDomain: false, domainsSaving: false, features: [], featureAdminAccess: "none",
      huddleLinks: huddleLinkSettings(undefined), huddleLinksSaving: false, chatOff: false,
    });
    this.ws = ws;
    this.deps = deps;
    this.photos = new PhotoPipeline(ws, deps.images, { type: "workspaceIcon" }, (fileId) => this.write({ iconFileId: fileId }), (photo) => {
      this.update({ photo });
      this.derive();
    });
    this.onDispose(() => this.photos.dispose());
    this.onDispose(ws.subscribe(() => this.follow()));
    this.follow();
  }

  /** The cached workspace (bootstrap + `workspace.updated`). */
  private follow(): void {
    const w = this.ws.state.cache?.workspace;
    if (w === undefined) return;
    const prev = this.state.workspace;
    if (prev !== undefined && JSON.stringify(prev) === JSON.stringify(w)) {
      this.derive();
      return;
    }
    this.adopt(w);
  }

  private adopt(w: Workspace): void {
    const editing = this.state.dirty;
    this.update({
      status: "ready",
      workspace: w,
      name: editing ? this.state.name : w.name,
      slug: editing ? this.state.slug : w.slug,
      defaultLocale: editing ? this.state.defaultLocale : w.defaultLocale ?? "en",
    });
    this.derive();
  }

  async load(): Promise<void> {
    void this.loadDomains();
    try {
      const w = await this.deps.api.workspace(this.ws.wsId);
      if (!this.isDisposed) this.adopt(w);
    } catch {
      if (!this.isDisposed && this.state.workspace === undefined) this.update({ status: "error" });
    }
  }

  private derive(): void {
    const s = this.state;
    const w = s.workspace;
    const cache = this.ws.state.cache;
    const me = cache?.members[this.ws.state.meId];
    const now = this.ws.deps.clock.now();
    const next: WorkspaceSettingsState = { ...s };
    next.canEdit = me !== undefined && canEditWorkspace(me, now);
    next.canDelete = me !== undefined && w !== undefined && cache !== undefined && canDeleteWorkspace(me, roleContext(w, Object.values(cache.members), now));
    const name = s.name.trim();
    const slug = s.slug.trim().toLowerCase();
    if (name === "") next.nameProblem = msg("workspace.settings.nameRequired");
    else delete next.nameProblem;
    const slugChanged = w !== undefined && slug !== w.slug;
    if (!slugChanged) delete next.slugProblem;
    else if (!isValidSlug(slug)) next.slugProblem = msg("workspace.settings.slugInvalid");
    else if (isReservedSlug(slug)) next.slugProblem = msg("workspace.settings.slugReserved");
    else if (slug === this.takenSlug) next.slugProblem = msg("workspace.settings.slugTaken");
    else delete next.slugProblem;
    if (slugChanged && w !== undefined) next.oldSlug = w.slug;
    else delete next.oldSlug;
    next.dirty = w !== undefined && (name !== w.name || slugChanged || s.defaultLocale !== (w.defaultLocale ?? "en"));
    next.canSave = next.canEdit && next.dirty && next.nameProblem === undefined && next.slugProblem === undefined && !s.saving;
    const icon = s.photo.phase === "uploading" && s.photo.preview !== undefined ? s.photo.preview : w?.iconUrl;
    if (icon === undefined) delete next.iconUrl;
    else next.iconUrl = icon;
    const primary = w?.primaryOwnerId === undefined ? undefined : cache?.members[w.primaryOwnerId];
    if (primary === undefined) delete next.primaryOwnerName;
    else next.primaryOwnerName = primary.displayName;
    const problem = this.draftProblem(s.domainDraft, s.domains ?? []);
    if (problem === undefined) delete next.domainProblem;
    else next.domainProblem = problem;
    next.canAddDomain = next.canEdit && s.domains !== undefined && s.domainDraft.trim() !== "" && problem === undefined && !s.domainsSaving;
    if (cache?.aiAvailable === true && w !== undefined) next.aiSummaries = { on: w.aiThreadSummaries !== false, saving: s.aiSummaries?.saving === true };
    else delete next.aiSummaries;
    if (w !== undefined) next.unfurls = { on: w.unfurlLinks !== false, saving: s.unfurls?.saving === true };
    else delete next.unfurls;
    if (w !== undefined) next.mailSentWith = { on: w.mailSentWith !== false, saving: s.mailSentWith?.saving === true };
    else delete next.mailSentWith;
    const features = w?.features;
    next.featureAdminAccess = w?.featureAdminAccess ?? "none";
    next.huddleLinks = huddleLinkSettings(w?.huddleLinks);
    next.chatOff = w !== undefined && !isFeatureEnabled(features, "chat");
    next.features = w === undefined ? [] : FEATURE_AREAS.map((area) => {
      const parent = FEATURE_PARENT[area];
      const on = features?.[area] !== false;
      const row: FeatureRow = { area, on, last: on && !canSetFeature(features, area, false) };
      if (parent !== undefined && !isFeatureEnabled(features, parent)) row.needs = parent;
      return row;
    });
    this.setState(next);
  }

  // ---------- Allowed email domains ----------

  private draftProblem(draft: string, domains: string[]): MessageDescriptor | undefined {
    if (draft.trim() === "") return undefined;
    const problem = emailDomainProblem(draft);
    if (problem === "invalid") return msg("workspace.settings.domainInvalid");
    if (problem === "public") return msg("workspace.settings.domainPublic", { domain: normalizeEmailDomain(draft) ?? draft.trim() });
    if (domains.includes(normalizeEmailDomain(draft)!)) return msg("workspace.settings.domainDuplicate");
    if (domains.length >= MAX_EMAIL_DOMAINS) return msg("workspace.settings.domainLimit", { count: MAX_EMAIL_DOMAINS });
    return undefined;
  }

  async loadDomains(): Promise<void> {
    try {
      const domains = await this.deps.api.emailDomains(this.ws.wsId);
      if (this.isDisposed) return;
      this.update({ domains });
      this.derive();
    } catch {
      // Guests can't see them; offline keeps the section hidden until the next visit.
    }
  }

  setDomainDraft(v: string): void {
    this.update({ domainDraft: v });
    this.derive();
  }

  /** Adds the draft (validated as typed; the server validates again) and saves. */
  async addDomain(): Promise<void> {
    const s = this.state;
    if (!s.canAddDomain || s.domains === undefined) return;
    const domain = normalizeEmailDomain(s.domainDraft)!;
    if (await this.writeDomains([...s.domains, domain])) {
      this.update({ domainDraft: "" });
      this.derive();
    }
  }

  async removeDomain(domain: string): Promise<void> {
    const s = this.state;
    if (!s.canEdit || s.domains === undefined || s.domainsSaving) return;
    await this.writeDomains(s.domains.filter((d) => d !== domain));
  }

  private async writeDomains(next: string[]): Promise<boolean> {
    this.update({ domainsSaving: true });
    this.derive();
    try {
      const domains = await this.deps.api.updateEmailDomains(this.ws.wsId, next);
      if (!this.isDisposed) this.update({ domains, notice: msg("settings.saved") });
      return true;
    } catch (e) {
      if (this.isDisposed) return false;
      const code = (e as ApiFailure).code;
      const draft = this.state.domainDraft.trim();
      this.update({
        notice:
          code === "email_domain_public" ? msg("workspace.settings.domainPublic", { domain: draft })
          : code === "email_domain_invalid" ? msg("workspace.settings.domainInvalid")
          : code === "email_domain_limit" ? msg("workspace.settings.domainLimit", { count: MAX_EMAIL_DOMAINS })
          : errorKind(e) === "network" ? msg("settings.offline")
          : msg("settings.failed"),
      });
      return false;
    } finally {
      if (!this.isDisposed) {
        this.update({ domainsSaving: false });
        this.derive();
      }
    }
  }

  setName(v: string): void {
    this.update({ name: v });
    this.derive();
  }

  setSlug(v: string): void {
    this.update({ slug: v });
    this.derive();
  }

  setDefaultLocale(l: Locale): void {
    this.update({ defaultLocale: l });
    this.derive();
  }

  discard(): void {
    const w = this.state.workspace;
    if (w === undefined) return;
    this.update({ name: w.name, slug: w.slug, defaultLocale: w.defaultLocale ?? "en", notice: undefined });
    this.derive();
  }

  async save(): Promise<void> {
    const s = this.state;
    const w = s.workspace;
    if (!s.canSave || w === undefined) return;
    const req: UpdateWorkspaceRequest = {};
    const name = s.name.trim();
    const slug = s.slug.trim().toLowerCase();
    if (name !== w.name) req.name = name;
    if (slug !== w.slug) req.slug = slug;
    if (s.defaultLocale !== (w.defaultLocale ?? "en")) req.defaultLocale = s.defaultLocale;
    this.update({ saving: true });
    this.derive();
    try {
      await this.write(req);
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (this.isDisposed) return;
      if ((e as ApiFailure).code === "slug_taken") this.takenSlug = slug;
      this.update({ notice: (e as ApiFailure).code === "slug_taken" ? msg("workspace.settings.slugTaken") : errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    } finally {
      if (!this.isDisposed) {
        this.update({ saving: false });
        this.derive();
      }
    }
  }

  private async write(req: UpdateWorkspaceRequest): Promise<void> {
    const w = await this.deps.api.updateWorkspace(this.ws.wsId, req);
    if (this.isDisposed) return;
    this.update({ dirty: false });
    this.adopt(w);
  }

  // ---------- Icon ----------

  pickIcon(file: PickedFile): void {
    if (this.state.canEdit) void this.photos.pick(file);
  }

  setIconCrop(rect: CropArea): void {
    this.photos.setCrop(rect);
  }

  confirmIcon(): Promise<boolean> {
    return this.photos.confirm();
  }

  cancelIcon(): void {
    this.photos.cancel();
  }

  async removeIcon(): Promise<void> {
    if (!this.state.canEdit || this.state.workspace?.iconUrl === undefined) return;
    try {
      await this.write({ removeIcon: true });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    }
  }

  /** Turns AI thread summaries on or off for the workspace (off also deletes the ones already written). */
  async setAiSummaries(on: boolean): Promise<void> {
    const cur = this.state.aiSummaries;
    if (!this.state.canEdit || cur === undefined || cur.saving || cur.on === on) return;
    this.update({ aiSummaries: { on, saving: true } });
    try {
      await this.write({ aiThreadSummaries: on });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    } finally {
      if (!this.isDisposed) {
        this.update({ aiSummaries: { on: this.state.workspace?.aiThreadSummaries !== false, saving: false } });
        this.derive();
      }
    }
  }

  /** Lets members add "Sent with Bundle" to their emails, or leaves it off every message from the workspace. */
  async setMailSentWith(on: boolean): Promise<void> {
    const cur = this.state.mailSentWith;
    if (!this.state.canEdit || cur === undefined || cur.saving || cur.on === on) return;
    this.update({ mailSentWith: { on, saving: true } });
    try {
      await this.write({ mailSentWith: on });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    } finally {
      if (!this.isDisposed) {
        this.update({ mailSentWith: { on: this.state.workspace?.mailSentWith !== false, saving: false } });
        this.derive();
      }
    }
  }

  /** Turns link previews on or off for the workspace (off also removes the previews already shown). */
  async setUnfurls(on: boolean): Promise<void> {
    const cur = this.state.unfurls;
    if (!this.state.canEdit || cur === undefined || cur.saving || cur.on === on) return;
    this.update({ unfurls: { on, saving: true } });
    try {
      await this.write({ unfurlLinks: on });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    } finally {
      if (!this.isDisposed) {
        this.update({ unfurls: { on: this.state.workspace?.unfurlLinks !== false, saving: false } });
        this.derive();
      }
    }
  }

  /**
   * Switches a product area on or off for the whole workspace (owners and admins). Off hides it in every app and
   * blocks its routes; nothing is deleted. The last usable area can't be turned off.
   */
  async setFeature(area: FeatureArea, on: boolean): Promise<void> {
    const w = this.state.workspace;
    if (!this.state.canEdit || w === undefined || this.state.featureSaving !== undefined) return;
    if ((w.features?.[area] !== false) === on) return;
    if (!canSetFeature(w.features, area, on)) {
      this.update({ notice: msg("features.lastOne") });
      return;
    }
    this.update({ featureSaving: area });
    try {
      await this.write({ features: { [area]: on } });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) {
        const code = (e as ApiFailure).code;
        this.update({ notice: code === "feature_last_enabled" ? msg("features.lastOne") : errorKind(e) === "network" ? msg("settings.offline") : msg("features.error") });
      }
    } finally {
      if (!this.isDisposed) {
        this.update({ featureSaving: undefined });
        this.derive();
      }
    }
  }

  /** What owners and admins keep of switched-off areas: nothing, a read-only look, or full use. */
  async setFeatureAdminAccess(access: FeatureAdminAccess): Promise<void> {
    if (!this.state.canEdit || this.state.workspace === undefined || this.state.featureAdminAccess === access) return;
    this.update({ featureAdminAccess: access });
    try {
      await this.write({ featureAdminAccess: access });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: errorKind(e) === "network" ? msg("settings.offline") : msg("features.error") });
    } finally {
      if (!this.isDisposed) this.derive();
    }
  }

  /** Settings → Huddles (owners and admins): links on or off, what people outside may do, new links' "Who can join". */
  async setHuddleLinks(patch: Partial<HuddleLinkSettings>): Promise<void> {
    if (!this.state.canEdit || this.state.workspace === undefined || this.state.huddleLinksSaving) return;
    const before = this.state.huddleLinks;
    this.update({ huddleLinks: { ...before, ...patch }, huddleLinksSaving: true });
    try {
      await this.write({ huddleLinks: patch });
      if (!this.isDisposed) this.update({ notice: msg("settings.saved") });
    } catch (e) {
      if (!this.isDisposed) this.update({ huddleLinks: before, notice: errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed") });
    } finally {
      if (!this.isDisposed) {
        this.update({ huddleLinksSaving: false });
        this.derive();
      }
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
